import {
  CONTENT_ID_ALLOWLIST_LOADED,
  CONTENT_ID_ALLOWLIST_CLEAR,
} from '../../actionTypes';

import * as api from '../../apiHelper';
import { arriveNotice } from '../../noticeActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';
import { setFaviconLoading, setFaviconDefault } from '../../faviconActions';

// Reducer corresponding actions
function contentIdAllowlistClear() {
  return { type: CONTENT_ID_ALLOWLIST_CLEAR };
}

function contentIdAllowlistLoaded(allowlist) {
  return {
    type: CONTENT_ID_ALLOWLIST_LOADED,
    payload: { allowlist },
  };
}

// API
async function getContentIdAllowlistApi(token) {
  const response = await api.get({
    path: 'youtube-cid/whitelists',
    token,
    version: 'gateway',
  });
  return response.json();
}

async function addContentIdAllowlistChannelApi(token, channels, dispatch) {
  // Always send array format (even for single channel)
  const body = Array.isArray(channels) ? channels : [channels];
  const response = await api.post({
    path: 'youtube-cid/whitelists',
    token,
    body,
    version: 'gateway',
    customError: true,
    dispatch,
  });

  if (!response.ok) {
    const json = await response.json();
    const errorMessage = json.message || json.error || 'Failed to add channels';
    throw new Error(errorMessage);
  }

  return response.json();
}

async function updateContentIdAllowlistChannelApi(token, body, dispatch) {
  const response = await api.patch({
    path: 'youtube-cid/whitelists',
    token,
    body,
    version: 'gateway',
    customError: true,
    dispatch,
  });

  if (!response.ok) {
    const json = await response.json();
    const errorMessage = json.message || json.error || 'Failed to update channel';
    throw new Error(errorMessage);
  }

  return response.json();
}

async function deleteContentIdAllowlistChannelApi(token, channels, dispatch) {
  // Always send array format in channels field
  const body = { channels: Array.isArray(channels) ? channels : [channels] };
  const response = await api.post({
    path: 'youtube-cid/whitelists/bulk-delete',
    token,
    body,
    version: 'gateway',
    customError: true,
    dispatch,
  });

  if (!response.ok) {
    const json = await response.json();
    const errorMessage = json.message || json.error || 'Failed to delete channels';
    throw new Error(errorMessage);
  }

  return response.json();
}

// Helper
async function fetchContentIdAllowlist(dispatch, token) {
  try {
    const allowlist = await getContentIdAllowlistApi(token);
    dispatch(contentIdAllowlistLoaded(allowlist));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  } finally {
    dispatch(setFaviconDefault());
    dispatch(setFetchLoaderReset());
  }
}

async function addContentIdAllowlistChannel(dispatch, token, channels) {
  try {
    const result = await addContentIdAllowlistChannelApi(token, channels, dispatch);
    // Handle bulk response format
    const { summary = {}, conflicts = [], errors = [] } = result;
    // Build success message
    let successMessage = '';
    if (summary.successful > 0) {
      successMessage = `${summary.successful} channel(s) added successfully`;
    }
    // Build conflict message
    let conflictMessage = '';
    if (summary.conflicts > 0) {
      const conflictChannels = conflicts.map((c) => c.id || c.title || 'channel').join(', ');
      conflictMessage = `${summary.conflicts} channel(s) already exist: ${conflictChannels}`;
    }
    // Build error message with detailed error information
    let errorMessage = '';
    if (summary.errors > 0) {
      const errorDetails = errors.map((e, index) => {
        const channelId = e.channelId || e.id || 'Unknown channel';
        const errorMsg = e.error || e.message || 'Unknown error';
        return `${index + 1}. ${channelId}: ${errorMsg}`;
      }).join('\n');
      errorMessage = `${summary.errors} channel(s) failed:\n${errorDetails}`;
    }
    // Show appropriate notices
    if (successMessage) {
      dispatch(arriveNotice(successMessage, 'ok'));
    }
    if (conflictMessage) {
      dispatch(arriveNotice(conflictMessage, 'warning'));
    }
    if (errorMessage) {
      dispatch(arriveNotice(errorMessage, 'warning'));
    }
    // If no successful operations and no conflicts, show generic error
    if (summary.successful === 0 && summary.conflicts === 0 && summary.errors === 0) {
      dispatch(arriveNotice(result.message || 'Failed to add channels'));
    }
    // Refresh the list after adding
    await fetchContentIdAllowlist(dispatch, token);
    return result;
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message || 'Failed to add channels'));
    }
    // Don't re-throw to prevent uncaught promise rejection
    return null;
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
}

async function updateContentIdAllowlistChannel(dispatch, token, body) {
  try {
    const result = await updateContentIdAllowlistChannelApi(token, body, dispatch);
    dispatch(arriveNotice(result.message || 'Channel updated successfully', 'ok'));
    // Refresh the list after updating
    await fetchContentIdAllowlist(dispatch, token);
    return result;
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message || 'Failed to update channel'));
    }
    // Don't re-throw to prevent uncaught promise rejection
    return null;
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
}

async function deleteContentIdAllowlistChannel(dispatch, token, channels) {
  try {
    const result = await deleteContentIdAllowlistChannelApi(token, channels, dispatch);
    // Handle bulk response format
    const { summary = {}, failed = [] } = result;
    // Build success message
    let successMessage = '';
    if (summary.successful > 0) {
      successMessage = `${summary.successful} channel(s) removed successfully`;
    }
    // Build error message with detailed error information
    let errorMessage = '';
    if (summary.failed > 0) {
      const errorDetails = failed.map((f, index) => {
        const channelId = f.channelId || f.id || 'Unknown channel';
        const errorMsg = f.error || f.message || 'Unknown error';
        return `${index + 1}. ${channelId}: ${errorMsg}`;
      }).join('\n');
      errorMessage = `${summary.failed} channel(s) failed to delete:\n${errorDetails}`;
    }
    // Show appropriate notices
    if (successMessage) {
      dispatch(arriveNotice(successMessage, 'ok'));
    }
    if (errorMessage) {
      dispatch(arriveNotice(errorMessage, 'error'));
    }
    // If no successful operations, show generic error
    if (summary.successful === 0 && summary.failed === 0) {
      dispatch(arriveNotice(result.message || 'Failed to delete channels'));
    }
    // Refresh the list after deleting
    await fetchContentIdAllowlist(dispatch, token);
    return result;
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message || 'Failed to delete channels'));
    }
    // Don't re-throw to prevent uncaught promise rejection
    return null;
  } finally {
    dispatch(setSubmissionLoaderReset());
  }
}

// Public components actions
export function initiateFetchAllowlistRequest() {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading },
    } = getState();

    // Prevent API call if payment is pending
    if (paymentPending) return;

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchContentIdAllowlist(dispatch, token);
  };
}

export function clearContentIdAllowlistRequest() {
  return (dispatch) => {
    dispatch(contentIdAllowlistClear());
  };
}

export function addContentIdAllowlistChannelRequest(channels) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    // Ensure channels is always an array
    const channelsArray = Array.isArray(channels) ? channels : [channels];
    await addContentIdAllowlistChannel(dispatch, token, channelsArray);
  };
}

export function updateContentIdAllowlistChannelRequest(body) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    await updateContentIdAllowlistChannel(dispatch, token, body);
  };
}

export function deleteContentIdAllowlistChannelRequest(channels) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      fetchLoader: { submissionLoading },
    } = getState();

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    // Ensure channels is always an array
    const channelsArray = Array.isArray(channels) ? channels : [channels];
    await deleteContentIdAllowlistChannel(dispatch, token, channelsArray);
  };
}

